import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@teach/ui'
import { formatCreditsWithWord } from '@/features/ai/lib/money'
import { OverwriteConfirmDialog } from './OverwriteConfirmDialog'
import type { TranslationLauncher } from './useTranslationLauncher'
import { useMessages } from '@/i18n'
import { messages } from './messages'

export type TranslationDialog = 'refresh' | 'overwrite' | 'cancel' | null

interface Props {
  open: TranslationDialog
  onOpenChange: (d: TranslationDialog) => void
  launcher: TranslationLauncher
  onCancelRun: () => void
  /** Куда вернуть фокус: пункт меню, открывший диалог, уже размонтирован. */
  onCloseAutoFocus?: (e: Event) => void
}

interface ConfirmProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
  onCloseAutoFocus?: (e: Event) => void
}

/** Актуализация — мягкий старт, но в устаревших местах он заменит и ручные
 *  правки перевода: об этом спрашиваем с ценой. */
const RefreshDialog = ({ cost, ...p }: ConfirmProps & { cost?: string }) => {
  const m = useMessages(messages)
  return (
    <AlertDialog open={p.open} onOpenChange={p.onOpenChange}>
      <AlertDialogContent onCloseAutoFocus={p.onCloseAutoFocus}>
        <AlertDialogHeader>
          <AlertDialogTitle>{m.refreshTitle}</AlertDialogTitle>
          <AlertDialogDescription>
            {m.refreshBody}
            {cost ? m.cost(formatCreditsWithWord(cost)) : ''}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{m.cancel}</AlertDialogCancel>
          <AlertDialogAction onClick={p.onConfirm}>{m.refresh}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

/** Отмена прогона (§6.7): сделанное остаётся, а работа модели уже оплачена. */
const CancelRunDialog = (p: ConfirmProps) => {
  const m = useMessages(messages)
  return (
    <AlertDialog open={p.open} onOpenChange={p.onOpenChange}>
      <AlertDialogContent onCloseAutoFocus={p.onCloseAutoFocus}>
        <AlertDialogHeader>
          <AlertDialogTitle>{m.cancelRunTitle}</AlertDialogTitle>
          <AlertDialogDescription>{m.cancelRunBody}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{m.keepTranslating}</AlertDialogCancel>
          <AlertDialogAction onClick={p.onConfirm}>{m.cancelRun}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

/**
 * Диалоги перевода из меню языка. Живут РЯДОМ с `DropdownMenu`, не внутри
 * `DropdownMenuContent`: закрытие меню размонтирует его содержимое, и
 * диалог, открытый изнутри, исчез бы вместе с ним.
 *
 * Цена в подтверждении — по тому, что показано в панели (`showsImages`):
 * отмеченные картинки входят в сумму, снятые — нет. Старт отсюда —
 * `runConfirmed`: меню закрыто, и отказ приходит тостом.
 */
export const TranslationDialogs = ({
  open,
  onOpenChange,
  launcher,
  onCancelRun,
  onCloseAutoFocus,
}: Props) => {
  const { quote, showsImages } = launcher
  const close = (next: boolean) => !next && onOpenChange(null)
  return (
    <>
      <OverwriteConfirmDialog
        open={open === 'overwrite'}
        cost={showsImages ? quote.overwriteTotalCost : quote.overwriteCost}
        onOpenChange={close}
        onCloseAutoFocus={onCloseAutoFocus}
        onConfirm={() => {
          onOpenChange(null)
          launcher.runConfirmed(true)
        }}
      />
      <RefreshDialog
        open={open === 'refresh'}
        cost={showsImages ? quote.pendingTotalCost : quote.textCost}
        onOpenChange={close}
        onCloseAutoFocus={onCloseAutoFocus}
        onConfirm={() => {
          onOpenChange(null)
          launcher.runConfirmed(false)
        }}
      />
      <CancelRunDialog
        open={open === 'cancel'}
        onOpenChange={close}
        onConfirm={onCancelRun}
        onCloseAutoFocus={onCloseAutoFocus}
      />
    </>
  )
}
